import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import apiClient from '/src/api/client';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { countries } from 'countries-list';
import poslahLogo from '../../../assets/poslahlogo.png';
import '../../../css/auth-pages.css';

const MotionDiv = motion.div;
const MotionImg = motion.img;
const MotionButton = motion.button;

const RequestDemo = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    businessType: '',
    country: '',
    phone: '',
    numOutlets: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const countryList = Object.entries(countries).map(([code, country]) => ({
    code,
    name: country.name
  })).sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }
    
    try {
      const emailContent = `
        <h3>Demo Request Details:</h3>
        <ul>
          <li><strong>Name:</strong> ${formData.firstName} ${formData.lastName}</li>
          <li><strong>Email:</strong> ${formData.email}</li>
          <li><strong>Job Title:</strong> ${formData.jobTitle}</li>
          <li><strong>Business Type:</strong> ${formData.businessType}</li>
          <li><strong>Number of Outlets:</strong> ${formData.numOutlets}</li>
          <li><strong>Country:</strong> ${formData.country}</li>
          <li><strong>Phone:</strong> ${formData.phone}</li>
        </ul>
        <h4>Message:</h4>
        <p>${formData.message}</p>
      `;
  
      const response = await apiClient.post('/api/v1/users/demo-request', {
        subject: 'New Demo Request from POSlah!',
        message: emailContent,
        senderEmail: formData.email,
        senderName: `${formData.firstName} ${formData.lastName}`
      });
  
      if (response.status === 200) {
        setSuccess(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          jobTitle: '',
          businessType: '',
          country: '',
          phone: '',
          numOutlets: '',
          message: ''
        });
      }
    } catch (err) {
      console.error('Demo request error:', err);
      setError(
        err.response?.data?.message || 
        'Failed to send demo request. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="demo-page">
      {/* Animated Background */}
      <div className="animated-bg"></div>
      
      {/* Floating Elements */}
      <div className="floating-elements">
        <MotionDiv 
          className="floating-element"
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <MotionDiv 
          className="floating-element"
          animate={{
            y: [20, -20, 20],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <MotionDiv 
        className="demo-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Logo Section */}
        <MotionDiv className="logo-section" variants={itemVariants}>
          <Link to="/">
            <MotionImg 
              src={poslahLogo} 
              alt="POSlah Logo"
              className="demo-logo"
              whileHover={{ scale: 1.05, rotate: 2 }}
              transition={{ duration: 0.3 }}
            />
          </Link>
        </MotionDiv>

        {/* Messages */}
        {error && (
          <MotionDiv 
            className="error-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <i className="bi bi-exclamation-triangle"></i>
            {error}
          </MotionDiv>
        )}

        {success && (
          <MotionDiv 
            className="success-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <i className="bi bi-check-circle"></i>
            Demo request sent successfully! We'll contact you soon.
          </MotionDiv>
        )}

        <div className="demo-content">
          {/* Left Column */}
          <MotionDiv className="demo-left-column" variants={itemVariants}>
            <h1 className="demo-title">Ready to See POSlah! in Action?</h1>
            
            <div className="feature-blocks">
              <MotionDiv className="feature-block" variants={itemVariants}>
                <div className="feature-icon">
                  <i className="bi bi-calendar-check"></i>
                </div>
                <div className="feature-content">
                  <h3>Schedule a personalized demo</h3>
                  <p>Explore AI tools and features tailored to your needs.</p>
                </div>
              </MotionDiv>

              <MotionDiv className="feature-block" variants={itemVariants}>
                <div className="feature-icon">
                  <i className="bi bi-currency-dollar"></i>
                </div>
                <div className="feature-content">
                  <h3>Get pricing details</h3>
                  <p>Affordable options for your hawker stalls.</p>
                </div>
              </MotionDiv>

              <MotionDiv className="feature-block" variants={itemVariants}>
                <div className="feature-icon">
                  <i className="bi bi-gear"></i>
                </div>
                <div className="feature-content">
                  <h3>Explore practical applications</h3>
                  <p>Discover how POSlah! optimizes operations and enhances business efficiency.</p>
                </div>
              </MotionDiv>
            </div>
          </MotionDiv>

          {/* Right Column - Form */}
          <MotionDiv className="demo-right-column" variants={itemVariants}>
            <div className="demo-form-card">
              <h2 className="form-title">Request Your Demo</h2>
              
              <form onSubmit={handleSubmit} className="demo-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-input"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <span className="input-highlight"></span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-input"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                    <span className="input-highlight"></span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-highlight"></span>
                </div>

                <div className="form-group">
                  <label className="form-label">Job Title</label>
                  <input
                    type="text"
                    className="form-input"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    required
                  />
                  <span className="input-highlight"></span>
                </div>

                <div className="form-group">
                  <label className="form-label">Business Type</label>
                  <select
                    className="form-input"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Business Type</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="retail">Retail Store</option>
                    <option value="cafe">Café</option>
                    <option value="service">Service-Based Business</option>
                    <option value="other">Other</option>
                  </select>
                  <span className="input-highlight"></span>
                </div>

                <div className="form-group">
                  <label className="form-label">Number of Outlets</label>
                  <select
                    className="form-input"
                    name="numOutlets"
                    value={formData.numOutlets}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Number of Outlets</option>
                    <option value="1">1</option>
                    <option value="2-5">2-5</option>
                    <option value="6-10">6-10</option>
                    <option value="10+">10+</option>
                  </select>
                  <span className="input-highlight"></span>
                </div>

                <div className="form-group">
                  <label className="form-label">Country</label>
                  <select
                    className="form-input"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Country</option>
                    {countryList.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <span className="input-highlight"></span>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <PhoneInput
                    country={'sg'}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    containerClass="phone-input-container"
                    inputClass="form-input phone-input"
                    buttonClass="phone-dropdown-button"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-input"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                  <span className="input-highlight"></span>
                </div>

                <MotionButton
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    'Request Demo'
                  )}
                </MotionButton>
              </form>
            </div>
          </MotionDiv>
        </div>
      </MotionDiv>
    </div>
  );
};

export default RequestDemo;